import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { authApi, authQueries, type LoginInput } from '../api/resources/auth';
import type { Viewer } from '../api/types';
import { clearIntroPending } from '../features/auth/registerWizard';

interface SessionContextValue {
  viewer: Viewer | null;
  isAuthenticated: boolean;
  isRestoring: boolean;
  isBanned: boolean;
  login: (input: LoginInput) => Promise<Viewer>;
  logout: () => Promise<void>;
  /**
   * Rendering hint only. Every mutation is re-authorized by the server.
   * Regular players have an empty permission list; this is true when signed in
   * and not account-banned.
   */
  can: (capability?: string) => boolean;
  refresh: () => Promise<unknown>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = useQuery(authQueries.me());

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (viewer) => {
      queryClient.setQueryData(authQueries.me().queryKey, viewer);
      void queryClient.invalidateQueries();
    },
  });

  const logout = useCallback(async () => {
    await authApi.logout();
    clearIntroPending();
    queryClient.setQueryData(authQueries.me().queryKey, null);
    queryClient.clear();
  }, [queryClient]);

  const viewer = meQuery.data ?? null;
  const accountBanned = Boolean(viewer?.ban && viewer.ban.scope === 'account');

  const value = useMemo<SessionContextValue>(
    () => ({
      viewer,
      isAuthenticated: viewer !== null,
      isRestoring: meQuery.isPending,
      isBanned: accountBanned,
      login: loginMutation.mutateAsync,
      logout,
      can: () => viewer !== null && !accountBanned,
      refresh: () => queryClient.invalidateQueries({ queryKey: authQueries.me().queryKey }),
    }),
    [viewer, meQuery.isPending, accountBanned, loginMutation.mutateAsync, logout, queryClient],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
